import "./submission-accordion.scss";
import { P2Accordion } from "react-bootstrap-common-script";
import { Separator } from "../../../../separator";

import { Helpers } from "../../../../../utils/helper";
import { StageDocumentsInfo, StageDMessageInfo } from "../../../../../models/classes/stage-d/Review-Application.class";
import { SolicitorCompletionResponse, SolicitorLodgementResponse } from "../../../../../models/classes/solicitor/SolicitorCompletionDetail.class";
import moment from "moment";
import { SubmitDocuments } from "../../../../../models/classes/CooSubmission.class";
import { useAppSelector } from "../../../../../redux/hook";
import { GroupSubmittedDocument } from "../../../../../models/classes/solicitor/SolicitorSubmission.class";
import { DocumentRow } from "./document-row";
import useIsMobile from "../../../../../hooks/use-is-mobile";
import { INSTRUMENT_TYPE_MAP } from "../../../../../utils/shared/constants";

interface SubmissionProps {
  submission: SolicitorCompletionResponse | SolicitorLodgementResponse | StageDocumentsInfo | StageDMessageInfo | GroupSubmittedDocument;
}

export default function SubmissionAccordion({ submission }: Readonly<SubmissionProps>) {
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const { isMobile } = useIsMobile();

  if (submission instanceof StageDMessageInfo) {
    return (
      <>
        <P2Accordion
          defaultOpen={false}
          hasShadow={false}
          title={
            <div>
              <p className="polymer-submission-accordion__badge">Messages</p>
              <p className="polymer-submission-accordion__title">{submission.subject}</p>
            </div>
          }
        >
          <div className="polymer-submission-accordion__section" style={{ marginBottom: 16 }}>
            <div className="polymer-submission-accordion__title-date-container">
              <p className="polymer-submission-accordion__section-title">HDB message</p>
              <p className="polymer-submission-accordion__date">Received on {Helpers.formatDate(submission.received_date, "long", true)}</p>
            </div>
            <p className="polymer-submission-accordion__message">{submission.message}</p>
          </div>

          <div className="polymer-submission-accordion__section highlight">
            <div className="polymer-submission-accordion__title-date-container">
              <p className="polymer-submission-accordion__section-title">Your reply</p>
              <p className="polymer-submission-accordion__date">
                Replied on {Helpers.formatDate(submission.replied_at, "long", true)} by {submission.replied_by}
              </p>
            </div>
            <p className="polymer-submission-accordion__message">{submission.reply}</p>
          </div>
        </P2Accordion>
        <Separator variant="dashed" />
      </>
    );
  }

  if (submission instanceof StageDocumentsInfo) {
    const latestComment = getLatestComment(submission);

    return (
      <>
        <P2Accordion
          defaultOpen={false}
          hasShadow={false}
          title={
            <div>
              <p className="polymer-submission-accordion__badge">Documents</p>
              <p className="polymer-submission-accordion__title">{submission.name}</p>
            </div>
          }
        >
          <div className="polymer-submission-accordion__section" style={{ marginBottom: 16 }}>
            <div className="polymer-submission-accordion__title-date-container">
              <p className="polymer-submission-accordion__section-title">HDB comment</p>
              <p className="polymer-submission-accordion__date">Received on {Helpers.formatDate(submission.received_date, "long", true)}</p>
            </div>
            <div style={{ marginBottom: 16 }}>
              {submission.comments.map((comment) => (
                <p key={comment.uuid}>{comment.text}</p>
              ))}
            </div>
          </div>
          <div className="polymer-submission-accordion__section highlight">
            <div className="polymer-submission-accordion__title-date-container">
              <p className="polymer-submission-accordion__section-title">Your reply</p>
              <p className="polymer-submission-accordion__date">
                Submitted on {Helpers.formatDate(latestComment.replied_at, "long", true)} by {latestComment.replied_by}
              </p>
            </div>
            <div className="polymer-submission-accordion__document-grid">
              {!isMobile ? (
                <>
                  <p className="polymer-submission-accordion__document-grid-header">File name</p>
                  <p className="polymer-submission-accordion__document-grid-header">Size</p>
                </>
              ) : null}
              {submission.documents?.map(({ document_type, file_name, file_size, key }, index) => {
                const isLastChild = !!submission.documents && index === submission.documents.length - 1;
                return (
                  <DocumentRow
                    key={key}
                    downloadKey={key}
                    singpassToken={singpassToken}
                    documentType={document_type}
                    fileName={file_name}
                    fileSize={file_size}
                    isLastChild={isLastChild}
                  />
                );
              })}
            </div>
          </div>
        </P2Accordion>
        <Separator variant="dashed" />
      </>
    );
  }

  if (submission instanceof GroupSubmittedDocument) {
    return (
      <>
        <P2Accordion
          defaultOpen={false}
          hasShadow={false}
          title={
            <div>
              <p className="polymer-submission-accordion__badge">Documents</p>
              <p className="polymer-submission-accordion__title">{submission.title}</p>
            </div>
          }
        >
          <div className="polymer-submission-accordion__section highlight">
            <div className="polymer-submission-accordion__title-date-container">
              <p className="polymer-submission-accordion__section-title">Your submission</p>
              <p className="polymer-submission-accordion__date">
                Submitted on {Helpers.formatDate(submission.submitted_date_time, "long", true)} by {submission.submitted_by}
              </p>
            </div>
            <div className="polymer-submission-accordion__document-grid">
              {!isMobile ? (
                <>
                  <p className="polymer-submission-accordion__document-grid-header">File name</p>
                  <p className="polymer-submission-accordion__document-grid-header">Size</p>
                </>
              ) : null}
              {submission.documents.map(({ key, file_content }, index) => {
                const isLastChild = !!submission.documents && index === submission.documents.length - 1;
                return (
                  <DocumentRow
                    key={key}
                    downloadKey={key}
                    singpassToken={singpassToken}
                    documentType={file_content.document_type}
                    fileName={file_content.file_name}
                    fileSize={file_content.size}
                    isLastChild={isLastChild}
                  />
                );
              })}
            </div>
            {submission.documents.some((document) => document.code === "SDC01") ? (
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <p className="polymer-submission-accordion__additional-info-title">Cashier's order for loan redemption in favour of</p>
                <p className="polymer-submission-accordion__additional-info-value">DBS for {submission.additional_info.mortgage_payee}</p>
              </div>
            ) : null}
          </div>
        </P2Accordion>
        <Separator variant="dashed" />
      </>
    );
  }

  if (submission instanceof SolicitorCompletionResponse) {
    const listOfDocuments = groupDocumentsBasedOnType(submission.documents);

    return (
      <>
        <P2Accordion
          defaultOpen={false}
          hasShadow={false}
          title={
            <div>
              <p className="polymer-submission-accordion__badge">Completion</p>
              <p className="polymer-submission-accordion__title">Completion Details</p>
            </div>
          }
        >
          <div className="polymer-submission-accordion__section highlight" style={{ marginBottom: 16 }}>
            <div className="polymer-submission-accordion__title-date-container">
              <p className="polymer-submission-accordion__section-title">Your submission</p>
              <p className="polymer-submission-accordion__date">
                Submitted on {Helpers.formatDate(submission.submitted_at, "long", true)} by {submission.submitted_by}
              </p>
            </div>
            <div className="polymer-submission-accordion__completion-grid" style={{ marginBottom: 16 }}>
              <p className="polymer-submission-accordion__completion-grid-name">Actual completion date</p>
              <p className="polymer-submission-accordion__completion-grid-value">{Helpers.formatDate(submission.actual_completion_date, "short", false)}</p>
            </div>
            {listOfDocuments.map((documents) => (
              <>
                <Separator variant="dashed" style={{ marginBottom: 16 }} />
                <p className="polymer-submission-accordion__completion-grid-name" style={{ marginBottom: 16 }}>
                  {documents[0].label}
                </p>
                <div className="polymer-submission-accordion__document-grid" style={{ marginBottom: 16 }}>
                  {!isMobile ? (
                    <>
                      <p className="polymer-submission-accordion__document-grid-header">File name</p>
                      <p className="polymer-submission-accordion__document-grid-header">Size</p>
                    </>
                  ) : null}
                  {documents.map(({ document_type, file_name, file_size, key }) => (
                    <DocumentRow
                      key={key}
                      downloadKey={key}
                      singpassToken={singpassToken}
                      documentType={document_type}
                      fileName={file_name}
                      fileSize={file_size}
                    />
                  ))}
                </div>
              </>
            ))}
          </div>
        </P2Accordion>
        <Separator variant="dashed" />
      </>
    );
  }

  if (submission instanceof SolicitorLodgementResponse) {
    const listOfDocuments = groupDocumentsBasedOnType(submission.documents);

    return (
      <>
        <P2Accordion
          defaultOpen={false}
          hasShadow={false}
          title={
            <div>
              <p className="polymer-submission-accordion__badge">Lodgement</p>
              <p className="polymer-submission-accordion__title">Lodgement Details</p>
            </div>
          }
        >
          <div className="polymer-submission-accordion__section highlight" style={{ marginBottom: 16 }}>
            <div className="polymer-submission-accordion__title-date-container">
              <p className="polymer-submission-accordion__section-title">Your submission</p>
              <p className="polymer-submission-accordion__date">
                Submitted on {Helpers.formatDate(submission.submitted_at, "long", true)} by {submission.submitted_by}
              </p>
            </div>
            <div className="polymer-submission-accordion__completion-grid" style={{ marginBottom: 16 }}>
              {submission.instruments.map((instrument) => (
                <>
                  <p className="polymer-submission-accordion__completion-grid-name">Instrument type</p>
                  <p className="polymer-submission-accordion__completion-grid-value">{INSTRUMENT_TYPE_MAP.get(instrument.instrument_type) ?? ""}</p>
                  <p className="polymer-submission-accordion__completion-grid-name">Instrument number</p>
                  <p className="polymer-submission-accordion__completion-grid-value">{instrument.number}</p>
                  <p className="polymer-submission-accordion__completion-grid-name">Date registrered with SLA</p>
                  <p className="polymer-submission-accordion__completion-grid-value">{Helpers.formatDate(instrument.registration_date, "long", false)}</p>
                  <div className="polymer-submission-accordion__completion-grid-separator" />
                </>
              ))}
              <p className="polymer-submission-accordion__completion-grid-name">Lodgement date</p>
              <p className="polymer-submission-accordion__completion-grid-value">{Helpers.formatDate(submission.lodgement_date, "long", false)}</p>
              <div className="polymer-submission-accordion__completion-grid-separator" />
            </div>
            {listOfDocuments.map((documents) => (
              <>
                <Separator variant="dashed" style={{ marginBottom: 16 }} />
                <p className="polymer-submission-accordion__completion-grid-name" style={{ marginBottom: 16 }}>
                  {documents[0].label}
                </p>
                <div className="polymer-submission-accordion__document-grid" style={{ marginBottom: 16 }}>
                  {!isMobile ? (
                    <>
                      <p className="polymer-submission-accordion__document-grid-header">File name</p>
                      <p className="polymer-submission-accordion__document-grid-header">Size</p>
                    </>
                  ) : null}
                  {documents.map(({ document_type, file_name, file_size, key }) => (
                    <DocumentRow
                      key={key}
                      downloadKey={key}
                      singpassToken={singpassToken}
                      documentType={document_type}
                      fileName={file_name}
                      fileSize={file_size}
                    />
                  ))}
                </div>
              </>
            ))}
          </div>
        </P2Accordion>
        <Separator variant="dashed" />
      </>
    );
  }

  return null;
}

function getLatestComment(stageDocumentsInfo: StageDocumentsInfo) {
  const comments = stageDocumentsInfo.comments;

  let latestComment = comments[0];
  let latestRepliedAt = moment(comments[0].replied_at);

  for (let i = 1; i < comments.length; i++) {
    const repliedAt = moment(comments[i].replied_at);
    if (latestRepliedAt.diff(repliedAt)) {
      latestComment = comments[i];
      latestRepliedAt = repliedAt;
    }
  }

  return latestComment;
}

function groupDocumentsBasedOnType(documents: SubmitDocuments[]): SubmitDocuments[][] {
  const documentMap = new Map<string, SubmitDocuments[]>();

  documents.forEach((document) => {
    const code = document.code;

    if (!code) return;

    const isKeyPresent = documentMap.has(code);

    if (isKeyPresent) {
      documentMap.get(code)?.push(document);
    } else {
      documentMap.set(code, [document]);
    }
  });

  const sortedEntries = Array.from(documentMap.entries()).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

  return sortedEntries.map((entry) => entry[1]);
}

// Round to 2 decimal places in megabyte
function getMbValue(num: number | null | undefined): string {
  if (!num) return "0 MB";

  return (num / 1000000).toFixed(2) + " MB";
}
